import React, { ReactElement } from 'react';
import { Heading } from '@appsumo/dorado-react';

import { DiscountDataType } from '~/types/account';
import { AutoApplyCouponWarning } from '~/components/account/Discounts';
import { CouponDisplay } from '~/components/account/Discounts';

interface CouponListProps {
  appliedCouponId?: number;
  coupons?: DiscountDataType['coupons'];
  isToggled?: boolean;
  lastCouponCode: string;
}

interface SavedCouponProps {
  appliedCouponId?: number;
  autoAppliedCouponDesc?: string;
  coupons?: DiscountDataType['coupons'];
  isToggled: boolean;
  lastCouponCode: string;
}

const MemoizedCoupons = React.memo(function CouponList({
  appliedCouponId,
  coupons,
  isToggled,
  lastCouponCode,
}: Readonly<CouponListProps>): ReactElement | null {
  if (!coupons?.length) return <></>;

  // Move the applied coupon to the top of the list
  const couponList = coupons.reduce(
    (m, coupon) => {
      const isCurrentlyApplied = appliedCouponId === coupon.id;
      const index =
        isCurrentlyApplied || lastCouponCode === coupon.code ? 0 : 1;

      m[index].push(
        <CouponDisplay
          key={coupon.id}
          coupon={coupon}
          isCurrentCouponApplied={isCurrentlyApplied}
          isAnyCouponApplied={!!appliedCouponId}
        />,
      );

      return m;
    },
    [[], []] as [ReactElement[], ReactElement[]],
  );

  const sortedList = [...couponList[0], ...couponList[1]];

  return !isToggled ? <>{sortedList.slice(0, 2)}</> : <>{sortedList}</>;
});

export default React.memo(function SavedCoupons({
  appliedCouponId,
  autoAppliedCouponDesc,
  coupons,
  isToggled,
  lastCouponCode,
}: Readonly<SavedCouponProps>): ReactElement {
  if (!coupons?.length) return <></>;

  return (
    <div className="grid gap-y-4">
      <Heading.H4>Coupons</Heading.H4>
      <p>
        Available coupons can be added to your cart at any time, however they
        cannot be stacked with any other coupons or promotional discounts.
        Applying a coupon to cart will override any previously applied discount.
      </p>
      {autoAppliedCouponDesc && (
        <AutoApplyCouponWarning couponDesc={autoAppliedCouponDesc} />
      )}
      <div className="grid gap-y-2">
        <MemoizedCoupons
          coupons={coupons}
          appliedCouponId={appliedCouponId}
          isToggled={isToggled}
          lastCouponCode={lastCouponCode}
        />
      </div>
    </div>
  );
});
